import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { TrendingUp, AlertTriangle, Pencil, X, Plus, Save } from 'lucide-react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Cell } from 'recharts';

// Compact currency for in-bar labels: "5.84Cr" (no ₹ symbol)
const fCompact = (v) => {
  if (!v || v === 0) return '';
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(0)}L`;
  return String(Math.round(n));
};

// Full currency for tooltip + narrative: "₹5.84Cr"
const fC = (v) => {
  if (!v && v !== 0) return '₹0';
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.?0+$/, '')}L`;
  return `₹${n.toFixed(0)}`;
};

const COLOR_GROWTH = '#2D8B5E';
const COLOR_DECLINE = '#D4726A';
const COLOR_CURRENT = '#378ADD';
const COLOR_TARGET_GAP = '#D4726A';

export default function RevenueTrendWidget({ divisionId, outletName, divisionName, subjectLabel, isCEO = true, historyUrl }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);

  // Resolve endpoint: prefer `historyUrl`, else divisions/{id}/history, else outlets/{name}/history.
  const endpoint = historyUrl
    || (outletName ? `/outlets/${encodeURIComponent(outletName)}/history` : null)
    || (divisionId ? `/divisions/${divisionId}/history` : null);
  const saveUrl = outletName
    ? `/outlets/${encodeURIComponent(outletName)}/history`
    : `/divisions/${divisionId}/history`;
  const title = subjectLabel || divisionName || outletName || 'Revenue Trend';

  const refresh = () => {
    if (!endpoint) return;
    api.get(endpoint).then(r => {
      setData(r.data);
      setRows((r.data.bars || []).filter(b => b.type === 'actual').map(b => ({
        fiscal_year: b.label, revenue_total: String(b.value || ''), note: b.note || '',
      })));
    }).catch(() => {});
  };

  useEffect(refresh, [endpoint]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const addRow = () => setRows(r => [...r, { fiscal_year: '', revenue_total: '', note: '' }]);
  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const updateRow = (i, field, val) => setRows(r => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  const save = async () => {
    for (const row of rows) {
      if (!row.fiscal_year.trim()) continue;
      try {
        await api.post(saveUrl, {
          fiscal_year: row.fiscal_year.trim(),
          revenue_total: parseFloat(row.revenue_total || 0),
          note: row.note || '',
        });
      } catch (err) { console.error(err); }
    }
    setEditing(false);
    refresh();
  };

  if (!data) return null;
  if (!isCEO && (!data.bars || data.bars.filter(b => b.type === 'actual').length === 0)) return null;

  const bars = data.bars || [];
  const curBar = bars.find(b => b.type === 'current');
  const prevCompletedBar = [...bars].reverse().find(b => b.type === 'actual');

  // ── Business-relevant warnings only ──
  // 1) "Declining trend" = only true if the LAST COMPLETED year dropped vs the
  //    one before it (not partial current year vs prior full year, which is
  //    not apples-to-apples).
  const actualBars = bars.filter(b => b.type === 'actual');
  const lastActualYoY = actualBars.length >= 2 ? actualBars[actualBars.length - 1].yoy_pct : null;
  const hasRealDecline = lastActualYoY != null && lastActualYoY < 0;
  const lastActual = actualBars[actualBars.length - 1];
  const prevActual = actualBars[actualBars.length - 2];

  // 2) Current FY pace warning — only trigger when we have >30 days in the FY
  //    AND pace is <60% of expected linear pace.
  const daysInFY = (() => {
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const start = new Date(y, 3, 1); // April 1
    return Math.max(1, Math.round((now - start) / 86400000));
  })();
  const expectedLinear = curBar?.target ? (curBar.target * daysInFY) / 365 : 0;
  const currentPace = curBar?.pct_of_target ?? 0;
  const paceBehind = daysInFY > 30 && expectedLinear > 0 && (curBar?.value || 0) < expectedLinear * 0.6;
  const toGo = curBar?.to_go ?? (curBar?.target ? Math.max(curBar.target - (curBar.value || 0), 0) : 0);

  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: hasRealDecline ? '#D4726A' : '#E5E1DC', borderWidth: hasRealDecline ? 2 : 1 }} data-testid="revenue-trend-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: '#1B2A4A' }}>
            <TrendingUp size={14} style={{ color: '#378ADD' }} />
            Revenue Trend — {title}
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>Multi-year revenue journey · current year shown vs target</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasRealDecline && (
            <span className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-semibold" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }} data-testid="decline-badge">
              <AlertTriangle size={11} /> {prevActual?.label}→{lastActual?.label}: {lastActualYoY}%
            </span>
          )}
          {paceBehind && !hasRealDecline && (
            <span className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-semibold" style={{ backgroundColor: '#FFFBEB', color: '#BA7517' }} data-testid="pace-badge">
              <AlertTriangle size={11} /> Pace behind ({currentPace}% on day {daysInFY})
            </span>
          )}
          {isCEO && (
            <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="trend-edit-btn">
              <Pencil size={11} /> Edit history
            </button>
          )}
        </div>
      </div>

      {bars.filter(b => b.type === 'actual').length === 0 && isCEO && (
        <div className="rounded-md p-3 text-center" style={{ backgroundColor: '#FFFBEB' }}>
          <p className="text-xs font-semibold" style={{ color: '#BA7517' }}>No history yet.</p>
          <p className="text-[11px] mt-0.5" style={{ color: '#92400E' }}>Click "Edit history" and paste 3-5 years of annual revenue to see the full trajectory.</p>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3" onClick={() => setEditing(false)}>
          <div className="bg-white rounded-t-xl sm:rounded-xl border max-w-2xl w-full p-4 space-y-3 max-h-[85vh] overflow-y-auto" style={{ borderColor: '#E5E1DC' }} onClick={e => e.stopPropagation()} data-testid="trend-edit-modal">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Revenue History — {title}</p>
              <button onClick={() => setEditing(false)}><X size={16} style={{ color: '#6B7280' }} /></button>
            </div>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>Add each fiscal year as a row. Use ₹ values (e.g. 82500000 = ₹8.25Cr). Blank values are ignored.</p>
            <div className="space-y-2">
              {rows.map((row, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input value={row.fiscal_year} onChange={e => updateRow(i, 'fiscal_year', e.target.value)} placeholder="FY24-25" className="col-span-3 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} data-testid={`trend-row-fy-${i}`} />
                  <input type="number" value={row.revenue_total} onChange={e => updateRow(i, 'revenue_total', e.target.value)} placeholder="82500000" className="col-span-4 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} data-testid={`trend-row-val-${i}`} />
                  <input value={row.note} onChange={e => updateRow(i, 'note', e.target.value)} placeholder="note (optional)" className="col-span-4 px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }} />
                  <button onClick={() => removeRow(i)} className="col-span-1 p-1 rounded hover:bg-red-50"><X size={13} style={{ color: '#D4726A' }} /></button>
                </div>
              ))}
            </div>
            <button onClick={addRow} className="flex items-center gap-1 text-xs px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="trend-add-row">
              <Plus size={12} /> Add year
            </button>
            <div className="flex gap-2 pt-2">
              <button onClick={save} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="trend-save"><Save size={12} /> Save history</button>
              <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded text-xs border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <RevenueChart bars={bars} isMobile={isMobile} />

      {/* Division-specific footnote (e.g. ME restated history) */}
      {divisionId === 'me' && actualBars.length > 0 && (
        <p className="text-[10px] italic pt-1" style={{ color: '#6B7280' }}>
          Note: FY25-26 and prior include Club Social B2B revenue. From FY26-27, Club Social is tracked as a separate division — apples-to-apples growth will resume next year.
        </p>
      )}

      {/* Narrative — always based on CURRENT-YEAR reality, never on fake YoY */}
      {curBar && curBar.target > 0 && (
        <div className="rounded-md p-3" style={{ backgroundColor: paceBehind ? '#FFFBEB' : '#F0FDF4', borderLeft: `3px solid ${paceBehind ? '#BA7517' : '#2D8B5E'}` }}>
          <p className="text-xs font-semibold" style={{ color: paceBehind ? '#7C2D12' : '#14532D' }}>
            {curBar.label}: {fC(curBar.value)} of {fC(curBar.target)} target ({currentPace}% · day {daysInFY} of 365)
          </p>
          <p className="text-[11px] mt-1" style={{ color: paceBehind ? '#92400E' : '#166534' }}>
            <strong>{fC(toGo)}</strong> to go. {paceBehind
              ? `Behind linear pace — needs to accelerate to close the gap.`
              : daysInFY < 30
              ? `Too early to judge — watch for 30-day trend.`
              : `On track or ahead of linear pace.`}
            {prevCompletedBar && ` Last completed: ${prevCompletedBar.label} closed at ${fC(prevCompletedBar.value)}.`}
          </p>
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// RevenueChart — bar + trend line + target overlay
// ────────────────────────────────────────────────────────────────
function RevenueChart({ bars, isMobile }) {
  if (!bars || bars.length === 0) return null;

  const chartData = bars.map(b => {
    const isCurrent = b.type === 'current';
    const isDecline = b.yoy_pct != null && b.yoy_pct < 0;
    const realised = b.value || 0;
    const target = isCurrent ? (b.target || 0) : 0;
    const contracted = isCurrent ? (b.contracted || 0) : 0;
    const gap = isCurrent ? Math.max(target - realised, 0) : 0;
    const topDisplay = isCurrent && target ? target : realised;
    const yoyLabel = b.yoy_pct == null ? '' : `${isDecline ? '▼' : '▲'}${b.yoy_pct > 0 ? '+' : ''}${b.yoy_pct}%`;
    return {
      label: b.label,
      realised,
      gap,
      target,
      contracted,
      realisedBySub: b.realised_by_sub || [],
      contractedBySub: b.contracted_by_sub || [],
      yoy: b.yoy_pct,
      yoyLabel,
      pctOfTarget: b.pct_of_target,
      valueLabel: isCurrent && target
        ? fCompact(target)
        : (realised > 0 ? fCompact(realised) : ''),
      isCurrent,
      isDecline,
      displayTop: topDisplay,
    };
  });

  const maxVal = Math.max(...chartData.map(d => Math.max(d.realised || 0, d.target || 0)), 1);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white border rounded-md px-3 py-2 shadow-sm text-[11px]" style={{ borderColor: '#E5E1DC' }}>
        <p className="font-semibold" style={{ color: '#1B2A4A' }}>{d.label}{d.isCurrent ? ' (current FY)' : ''}</p>
        {d.isCurrent && d.target > 0 ? (
          <div className="mt-1 space-y-0.5">
            <p className="font-semibold" style={{ color: COLOR_CURRENT }}>
              <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: COLOR_CURRENT }}></span>
              Actual (realised): {fC(d.realised)} ({d.pctOfTarget}% of target)
            </p>
            {d.contracted > 0 && (
              <p className="font-semibold" style={{ color: '#378ADD' }}>
                <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: '#378ADD' }}></span>
                Contracted: {fC(d.contracted)} ({d.target > 0 ? Math.round(((d.realised + d.contracted) / d.target) * 100) : 0}% locked incl. contracts)
              </p>
            )}
            <p className="font-semibold" style={{ color: '#1B2A4A' }}>
              <span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ backgroundColor: '#1B2A4A' }}></span>
              Target: {fC(d.target)}
            </p>
            <p className="font-semibold" style={{ color: COLOR_TARGET_GAP }}>
              <span className="inline-block w-2 h-2 rounded-sm mr-1 border border-dashed" style={{ borderColor: COLOR_TARGET_GAP }}></span>
              To Go: {fC(Math.max(d.target - d.realised, 0))}
            </p>
            {d.realisedBySub.length > 1 && (
              <div className="mt-2 pt-1 border-t" style={{ borderColor: '#F3F4F6' }}>
                <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: '#9CA3AF' }}>Actual (realised) by sub-division</p>
                {d.realisedBySub.map((s, i) => (
                  <p key={i} style={{ color: '#1B2A4A' }}>
                    <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ backgroundColor: COLOR_CURRENT }}></span>
                    {s.name}: {fC(s.value)}
                  </p>
                ))}
              </div>
            )}
            {d.contractedBySub.length > 1 && (
              <div className="mt-2 pt-1 border-t" style={{ borderColor: '#F3F4F6' }}>
                <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: '#378ADD' }}>Contracted by sub-division</p>
                {d.contractedBySub.map((s, i) => (
                  <p key={i} style={{ color: '#1B2A4A' }}>
                    <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 border" style={{ borderColor: '#378ADD' }}></span>
                    {s.name}: {fC(s.value)}
                  </p>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <p style={{ color: '#6B7280' }}>Revenue: {fC(d.realised)}</p>
            {d.yoy != null && (
              <p className="font-semibold" style={{ color: d.isDecline ? COLOR_DECLINE : COLOR_GROWTH }}>
                YoY: {d.yoy > 0 ? '+' : ''}{d.yoy}%
              </p>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="w-full" style={{ height: isMobile ? 280 : 320 }} data-testid="revenue-trend-chart">
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
        <ComposedChart data={chartData} margin={{ top: isMobile ? 32 : 40, right: isMobile ? 6 : 20, left: isMobile ? -8 : 0, bottom: isMobile ? 4 : 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: isMobile ? 9 : 11, fill: '#1B2A4A', fontWeight: 600 }}
            interval={0}
            angle={isMobile ? -30 : 0}
            textAnchor={isMobile ? 'end' : 'middle'}
            height={isMobile ? 46 : 24}
            axisLine={{ stroke: '#E5E1DC' }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => fCompact(v)}
            tick={{ fontSize: isMobile ? 9 : 10, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            domain={[0, Math.ceil(maxVal * 1.15)]}
            width={isMobile ? 36 : 56}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F9FAFB' }} />

          {/* Realised revenue (solid) — stacked below target-gap for current year */}
          <Bar dataKey="realised" stackId="a" barSize={isMobile ? 22 : 42} isAnimationActive={true}>
            {chartData.map((d, i) => (
              <Cell
                key={i}
                fill={d.isCurrent ? COLOR_CURRENT : d.isDecline ? COLOR_DECLINE : COLOR_GROWTH}
                fillOpacity={d.isCurrent ? 0.92 : 1}
                radius={d.isCurrent && d.gap > 0 ? 0 : 4}
              />
            ))}
          </Bar>

          {/* Target-gap overlay (dashed translucent) — only current FY */}
          <Bar
            dataKey="gap"
            stackId="a"
            barSize={isMobile ? 22 : 42}
            fill={COLOR_TARGET_GAP}
            fillOpacity={0.12}
            stroke={COLOR_TARGET_GAP}
            strokeDasharray="4 3"
            strokeWidth={1.25}
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          >
            <LabelList dataKey="yoyLabel" position="top" offset={isMobile ? 2 : 4} content={(props) => {
              const { x, y, width, value, index } = props;
              if (!value) return null;
              const d = chartData[index];
              if (d?.isCurrent) return null;
              const fill = d?.isDecline ? COLOR_DECLINE : COLOR_GROWTH;
              return (
                <text x={Number(x) + Number(width) / 2} y={Number(y) - (isMobile ? 2 : 4)} textAnchor="middle" fontSize={isMobile ? 8 : 10} fontWeight="600" fill={fill}>
                  {value}
                </text>
              );
            }} />
            <LabelList dataKey="valueLabel" position="top" offset={isMobile ? 18 : 22} content={(props) => {
              const { x, y, width, value, index } = props;
              if (!value) return null;
              const d = chartData[index];
              const isCur = d?.isCurrent;
              const display = isCur
                ? (isMobile ? `🎯${value}` : `Target ${value}`)
                : value;
              return (
                <text x={Number(x) + Number(width) / 2} y={Number(y) - (isMobile ? 14 : 18)} textAnchor="middle" fontSize={isMobile ? 9 : 11} fontWeight="700" fill={isCur ? COLOR_TARGET_GAP : '#1B2A4A'}>
                  {display}
                </text>
              );
            }} />
          </Bar>

          {/* Trend line through actual tops + target of current */}
          <Line
            type="monotone"
            dataKey="displayTop"
            stroke="#1B2A4A"
            strokeWidth={isMobile ? 1.5 : 2}
            strokeDasharray="6 4"
            dot={{ r: isMobile ? 2.5 : 4, fill: '#1B2A4A', stroke: '#fff', strokeWidth: isMobile ? 1 : 2 }}
            activeDot={{ r: isMobile ? 3.5 : 5 }}
            isAnimationActive={true}
            legendType="none"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
